import cog from "../../assets/cog.svg"
import { base64ToBlob } from "../shared/utils"
import Avatar from "./../../assets/avatar.svg"
import UnregisteredBadge from "../shared/components/UnregisteredBadge"
import { useNavigateWithQueryString } from "../../App"
import { appRoutes } from "../../routes"
import { useSelector } from "react-redux"
import { useSlideOver } from "../useSlideOver"
import { isAffiliateRelations } from "../authz/authz"
import ManagedBadge from "../shared/components/ManagedBadge"
import { ProfileStatusBadge } from "./shared/profileStatusBadge"
import { ProfileStatusEnum } from "./shared/profileStatusBadge"
import { getBase64ImageFromStorage } from "../shared/localStorageBase64"
import Link from "../shared/components/Link"
import ColumnContainer from "../shared/components/Layout/ColumnContainer"
import RowContainer from "../shared/components/Layout/RowContainer"
import Thumbnail from "../shared/components/Thumbnail"
import { Typography, typographyTypes } from "../shared/components/Typography"
import { ButtonWrapper } from "../shared/components/ButtonWrapper"
import Tag from "../shared/components/Tag"

interface HeaderData {
  name: string
  address?: string
  designations?: string[]
  image?: string // check type
  phones?: string[]
  profileId: string
  profileType: string
  tags?: string[]
  title?: string
  websiteDestination?: string
  websiteName?: string
  isManaged?: boolean
  isRegistered?: boolean
  status?: string // for practice
  providerStatusDisplayId?: string // for provider
}
interface SidebarHeaderProps {
  data: HeaderData
}

export const SlideOverHeader: React.FC<SidebarHeaderProps> = ({ data }) => {
  const {
    address,
    designations,
    image,
    name,
    phones,
    profileId,
    profileType,
    tags,
    title,
    websiteName,
    websiteDestination,
    isManaged = false,
    isRegistered = true,
  } = data

  const isARUser = useSelector(isAffiliateRelations)

  const { handleSetShowProfileSidebar } = useSlideOver()
  let objectURL = ""
  if (image) {
    const base64String = getBase64ImageFromStorage(image)
    if (base64String) {
      const fileBlob = base64ToBlob(base64String!)
      objectURL = fileBlob ? URL.createObjectURL(fileBlob) : ""
    }
  }

  const navigate = useNavigateWithQueryString()
  const handleClick = () => {
    handleSetShowProfileSidebar()
    if (profileType === "providerProfile") {
      navigate(
        appRoutes.provider.personalInformation.CONTACT_ROUTE,
        `?providerId=${profileId}`,
      )
    } else if (profileType === "practiceProfile") {
      navigate(
        appRoutes.practice.practiceInformation.ADDRESS_ROUTE,
        `?practiceId=${profileId}`,
      )
    }
  }
  return (
    <ColumnContainer styleOverrides="w-full">
      <RowContainer>
        {image && (
          <Thumbnail
            ariaLabel="provider picture"
            id={name}
            imageAltText="provider picture"
            imageUrl={objectURL}
            styleOverrides="min-w-[200px]"
            size="lg"
          />
        )}
        {profileType === "providerProfile" && !image && (
          <img
            src={Avatar}
            alt="No Image Available"
            className="w-48 h-32 ml-[-8px]"
          />
        )}
        <ColumnContainer styleOverrides="space-y-0">
          <RowContainer styleOverrides="mt-1 space-x-2 items-center">
            <Typography
              variant={typographyTypes.NORMAL}
              text={name}
              styleOverrides="text-2xl font-bold text-gray-900"
            />
            {isARUser && (
              <ButtonWrapper
                onClick={handleClick}
                ariaDescription={`link to ${profileType === "providerProfile" ? "provider" : "practice"} information`}
              >
                <img src={cog} alt={"link to profile maintenance"} />
              </ButtonWrapper>
            )}
          </RowContainer>
          {profileType === "providerProfile" && !isManaged && (
            <UnregisteredBadge force={!isRegistered} providerId={profileId} />
          )}
          {title && (
            <Typography
              text={title}
              variant={typographyTypes.NORMAL}
              styleOverrides="text-base text-gray-700 !leading-8"
            />
          )}
          {address && (
            <Typography
              text={address}
              variant={typographyTypes.NORMAL}
              styleOverrides=""
            />
          )}
          <ColumnContainer styleOverrides="pt-2.5 space-y-[2px]">
            {designations && (
              <Typography
                text={designations.join(", ")}
                variant={typographyTypes.NORMAL}
                styleOverrides=""
              />
            )}
            {phones &&
              phones.map((phone) => {
                return (
                  <Typography
                    key={phone}
                    text={phone}
                    variant={typographyTypes.NORMAL}
                    styleOverrides=""
                  />
                )
              })}
            {websiteName && websiteDestination && (
              <Link
                destination={websiteDestination}
                style="default"
                text={websiteName}
                underlined
              />
            )}
            {/* Profile Status Row: ManagedBadge + StatusBadge */}
            <RowContainer styleOverrides="space-x-2 items-center pt-2">
              {isManaged && <ManagedBadge force={true} />}
              <>
                {profileType === "practiceProfile" && (
                  <ProfileStatusBadge
                    status={data.status as ProfileStatusEnum}
                  />
                )}
                {profileType === "providerProfile" &&
                  data.providerStatusDisplayId && (
                    <ProfileStatusBadge
                      status={data.providerStatusDisplayId as ProfileStatusEnum}
                    />
                  )}
              </>
            </RowContainer>
          </ColumnContainer>

          {tags && (
            <div className="py-3 flex-wrap flex">
              {tags.map((tag) => {
                return <Tag key={tag} text={tag} styleOverrides="mr-5 mt-2" />
              })}
            </div>
          )}
        </ColumnContainer>
      </RowContainer>
    </ColumnContainer>
  )
}
